/**
 * i18n 翻译合并工具函数
 * 
 * 提供模块翻译合并和语言资源合并的功能
 */

import type {
  LocaleModules,
  Resources,
  CommonTranslations,
  HomeTranslations,
  FeaturesTranslations,
  TechnologyTranslations,
  ProductsTranslations,
  QuoteTranslations,
  AboutTranslations,
  ContactTranslations,
  PrivacyTranslations,
  TermsTranslations,
  InstallationGuideTranslations,
  WarrantyPolicyTranslations,
  FaqTranslations,
  TranslationResources,
  ModularLocaleModules,
} from '../types';

/**
 * 深度合并两个对象，处理嵌套对象和数组
 */
function deepMerge<T extends Record<string, any>>(target: T, source: Partial<T>): T {
  const result = { ...target } as T;
  
  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      if (result[key] && typeof result[key] === 'object' && !Array.isArray(result[key])) {
        (result as any)[key] = deepMerge(result[key], source[key]);
      } else {
        (result as any)[key] = source[key];
      }
    } else {
      (result as any)[key] = source[key];
    }
  }
  
  return result;
}

/**
 * 合并模块翻译
 * 将各个模块的翻译合并为一个完整的翻译对象
 */
export function mergeModules(
  modules: {
    common: CommonTranslations;
    home: HomeTranslations;
    features: FeaturesTranslations;
    technology: TechnologyTranslations;
    products: ProductsTranslations;
    quote: QuoteTranslations;
    about: AboutTranslations;
    contact: ContactTranslations;
    privacy: PrivacyTranslations;
    terms: TermsTranslations;
    installationGuide: InstallationGuideTranslations;
    warrantyPolicy: WarrantyPolicyTranslations;
    faq: FaqTranslations;
  }
): TranslationResources {
  return {
    common: modules.common,
    home: modules.home,
    features: modules.features,
    technology: modules.technology,
    products: modules.products,
    quote: modules.quote,
    about: modules.about,
    contact: modules.contact,
    privacy: modules.privacy,
    terms: modules.terms,
    installationGuide: modules.installationGuide,
    warrantyPolicy: modules.warrantyPolicy,
    faq: modules.faq,
  };
}

/**
 * 创建完整的语言资源对象
 * 将各语言的模块翻译合并为 i18next 所需的资源格式
 */
export function createResources(
  locales: {
    en: LocaleModules;
    zh: LocaleModules;
    ar: LocaleModules;
    es: LocaleModules;
    pt: LocaleModules;
    fr: LocaleModules;
    ru: LocaleModules;
  }
): Resources {
  return {
    en: {
      translation: mergeModules(locales.en),
    },
    zh: {
      translation: mergeModules(locales.zh),
    },
    ar: {
      translation: mergeModules(locales.ar),
    },
    es: {
      translation: mergeModules(locales.es),
    },
    pt: {
      translation: mergeModules(locales.pt),
    },
    fr: {
      translation: mergeModules(locales.fr),
    },
    ru: {
      translation: mergeModules(locales.ru),
    },
  };
}

/**
 * 将对象中的 items 数组转换为 item1, item2, item3... 格式
 * Privacy 和 Terms 页面组件使用 item1, item2 等键名
 */
function expandItemsToKeys(obj: any): any {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj;
  
  const result = { ...obj };
  for (const key in result) {
    if (Array.isArray(result[key]) && key === 'items') {
      const arr = result[key];
      arr.forEach((item: string, index: number) => {
        (result as any)[`item${index + 1}`] = item;
      });
    } else if (result[key] && typeof result[key] === 'object' && !Array.isArray(result[key])) {
      result[key] = expandItemsToKeys(result[key]);
    }
  }
  return result;
}

/**
 * 将模块翻译平铺合并为一个完整的翻译对象
 * 将 common.nav、home.hero 等合并为 translation.nav、translation.hero
 */
export function flattenModules(
  modules: {
    common: CommonTranslations;
    home: HomeTranslations;
    features: FeaturesTranslations;
    technology: TechnologyTranslations;
    products: ProductsTranslations;
    quote: QuoteTranslations;
    about: AboutTranslations;
    contact: ContactTranslations;
    legal: { privacy: PrivacyTranslations; terms: TermsTranslations; installationGuide: InstallationGuideTranslations; warrantyPolicy: WarrantyPolicyTranslations };
    faq: FaqTranslations;
  }
): Record<string, any> {
  const result: Record<string, any> = {};
  
  // 展开 common 模块
  if (modules.common) {
    if (modules.common.nav) {
      Object.assign(result, { nav: modules.common.nav });
    }
    if (modules.common.footer) {
      Object.assign(result, { footer: modules.common.footer });
    }
  }
  
  // 展开 home 模块，同时添加 hero 作为顶层键（Hero 组件使用 hero.slides.xxx）
  if (modules.home) {
    Object.assign(result, { home: modules.home });
    if (modules.home.hero) {
      Object.assign(result, { hero: modules.home.hero });
    }
  }
  if (modules.features) {
    Object.assign(result, { features: modules.features });
  }
  if (modules.technology) {
    Object.assign(result, { technology: modules.technology });
  }
  if (modules.products) {
    Object.assign(result, { products: modules.products });
  }
  if (modules.quote) {
    Object.assign(result, { quote: modules.quote });
  }
  if (modules.about) {
    Object.assign(result, { about: modules.about });
  }
  if (modules.contact) {
    Object.assign(result, { contact: modules.contact });
  }
  if (modules.legal) {
    if (modules.legal.privacy) {
      Object.assign(result, { privacy: expandItemsToKeys(modules.legal.privacy) });
    }
    if (modules.legal.terms) {
      Object.assign(result, { terms: expandItemsToKeys(modules.legal.terms) });
    }
    if (modules.legal.installationGuide) {
      Object.assign(result, { installationGuide: modules.legal.installationGuide });
    }
    if (modules.legal.warrantyPolicy) {
      Object.assign(result, { warrantyPolicy: modules.legal.warrantyPolicy });
    }
  }
  if (modules.faq) {
    Object.assign(result, { faq: modules.faq });
  }
  
  return result;
}

/**
 * 创建单一语言的翻译资源
 * 用于动态导入单个语言模块
 * 将模块平铺合并为 i18next 所需的格式
 */
export function createLanguageResource(
  modules: ModularLocaleModules
): { translation: Record<string, any> } {
  return {
    translation: flattenModules(modules),
  };
}

/**
 * 安全获取嵌套对象属性
 * 如果路径不存在，返回默认值
 */
export function getNestedValue<T>(
  obj: Record<string, any>,
  path: string,
  defaultValue: T
): T {
  const keys = path.split('.');
  let current: any = obj;
  
  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      return defaultValue;
    }
  }
  
  return (current !== undefined ? current : defaultValue) as T;
}

/**
 * 验证翻译完整性
 * 检查必要的翻译键是否存在
 */
export function validateTranslationCompleteness(
  translation: TranslationResources,
  requiredKeys: string[]
): { complete: boolean; missingKeys: string[] } {
  const missingKeys: string[] = [];
  
  for (const key of requiredKeys) {
    const keys = key.split('.');
    let current: any = translation;
    
    for (const k of keys) {
      if (current && typeof current === 'object' && k in current) {
        current = current[k];
      } else {
        missingKeys.push(key);
        break;
      }
    }
  }
  
  return {
    complete: missingKeys.length === 0,
    missingKeys,
  };
}

/**
 * 创建带类型的翻译键访问器
 * 提供类型安全的翻译键访问
 */
export function createTranslationAccessor<T extends Record<string, any>>(
  translation: T
): T {
  // 返回原始翻译对象，但可以通过类型系统确保类型安全
  return translation;
}

/**
 * 导出默认的合并配置
 */
export const defaultMergeOptions = {
  arrays: 'replace' as const, // 数组替换策略
  nullish: 'keep' as const,  // null/undefined 保留原值
};
